/**
 * 設定 > Googleタグ タブ（owner のみ）。
 * 全公開ページ（ランディング・公開フォーム）に既定で注入される GTM 等のタグを設定する。
 * ランディング個別設定（編集 > 連携タブ）があればそちらが優先される。
 */
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useGoogleTagSettings,
  useUpdateGoogleTagSettings,
} from '@/hooks/useSettings';

export function GoogleTagTab() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { data, isLoading } = useGoogleTagSettings();
  const updateMutation = useUpdateGoogleTagSettings();

  const [enabled, setEnabled] = useState(false);
  const [headHtml, setHeadHtml] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  useEffect(() => {
    if (data) {
      setEnabled(data.enabled ?? false);
      setHeadHtml(data.headHtml ?? '');
      setBodyHtml(data.bodyHtml ?? '');
    }
  }, [data]);

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        enabled,
        headHtml: headHtml.trim(),
        bodyHtml: bodyHtml.trim(),
      });
      toast.success('Googleタグを保存しました');
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="max-w-2xl space-y-6">
      <section>
        <h3 className="text-base font-bold mb-2 pb-2 border-b border-[var(--border)]">
          Googleタグ（既定）
        </h3>
        <p className="text-xs text-[var(--text-muted)] leading-relaxed mb-4">
          広告代理店から受領した GTM の「Aタグ（head 用）」「Bタグ（body 用）」をそのまま貼り付けてください。
          ここで設定したタグは、<strong>すべてのランディングと公開フォーム</strong>に既定で挿入されます。
          <br />
          ランディングごとに個別のタグを設定したい場合は、ランディング編集画面の「連携」タブで上書きできます。
          <br />
          ※ 計測タグは公開ページの HTML に出力される公開情報です。シークレット（API キー等）は貼り付けないでください。
        </p>

        <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3">
          <Switch
            label="既定タグを有効にする"
            description="OFF にすると既定タグの挿入を停止します（個別設定タグには影響しません）。"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!isOwner}
          />
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <Label htmlFor="gtagHead">Aタグ（&lt;head&gt; 用）</Label>
          <Textarea
            id="gtagHead"
            rows={6}
            placeholder={'<!-- Google Tag Manager -->\n<script>(function(w,d,s,l,i){...})...</script>\n<!-- End Google Tag Manager -->'}
            value={headHtml}
            onChange={(e) => setHeadHtml(e.target.value)}
            disabled={!isOwner}
            spellCheck={false}
            className="font-mono text-[12px]"
          />
          <p className="text-xs text-[var(--text-muted)] mt-1">
            全ページの &lt;head&gt; のなるべく上に挿入されます。
          </p>
        </div>

        <div>
          <Label htmlFor="gtagBody">Bタグ（&lt;body&gt; 直下用）</Label>
          <Textarea
            id="gtagBody"
            rows={4}
            placeholder={'<!-- Google Tag Manager (noscript) -->\n<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-XXXX"...></iframe></noscript>\n<!-- End Google Tag Manager (noscript) -->'}
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            disabled={!isOwner}
            spellCheck={false}
            className="font-mono text-[12px]"
          />
          <p className="text-xs text-[var(--text-muted)] mt-1">
            全ページの &lt;body&gt; 直下に挿入されます。
          </p>
        </div>
      </section>

      {isOwner && (
        <div className="flex justify-end gap-2 pt-4 border-t border-[var(--border)]">
          <Button type="button" variant="primary" onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </div>
      )}
    </div>
  );
}
